import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Collapse } from "@ecco/ui/collapse";
import { Tooltip } from "@ecco/ui/tooltip";
import { breakpoints } from "@ecco/ui/tokens";
import { useOutsideClick } from "@ecco/ui/use-outside-click";
import { throttle } from "lodash-es";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

interface CalloutDiscountProps {
    title: string;
    description: string;
}

export const CalloutDiscount: FC<CalloutDiscountProps> = ({ title, description }) => {
    const [isOpen, setIsOpen] = useState(false);
    const tooltipRef = useRef(undefined);

    useOutsideClick({
        enabled: isOpen,
        ref: tooltipRef,
        handler: (event) => {
            event.stopPropagation();
            // If the click is outside the tooltip, close it, if the click is on the tooltip, do nothing, will be handled by the tooltip itself
            if (event.target !== tooltipRef.current) setIsOpen(false);
        },
    });

    // We need the effect and read the viewport width, the tooltip will behave differently on mobile and desktop
    useEffect(() => {
        if (!isOpen) return;
        const isDesktop = window.matchMedia(`(min-width: ${breakpoints.lg})`).matches;
        const handleScroll = () => {
            if (isDesktop) {
                setIsOpen(false);
            }
        };
        const throttleScroll = throttle(handleScroll, 200);
        document.addEventListener("scroll", throttleScroll, { passive: true });
        return () => {
            throttleScroll.cancel();
            document.removeEventListener("scroll", throttleScroll);
        };
    }, [isOpen]);

    return (
        <>
            <Flex
                position={"relative"}
                alignItems={"center"}
                justifyContent={"flex-start"}
                mb={{ base: 4, xl: 8 }}
                gap={2}
                mr={2}
                display={"flex"}
            >
                <Text fontSize={"desktopBodyTextSmall"} fontWeight="bold">
                    {title}
                </Text>
                {description && (
                    <Box alignSelf={"start"} zIndex={1}>
                        <Tooltip
                            display={{ base: "none", lg: "flex" }}
                            isOpen={isOpen}
                            hasArrow={false}
                            label={description}
                            p={4}
                            mr={4}
                            closeOnScroll={true}
                            aria-label={description}
                        >
                            <ECCOIcon
                                display={"block"}
                                ref={tooltipRef}
                                onClick={() => setIsOpen(!isOpen)}
                                cursor={"pointer"}
                                name={"info"}
                            />
                        </Tooltip>
                    </Box>
                )}
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box
                    display={{ base: "block", lg: "none" }}
                    fontSize={"desktopBodyTextTiny"}
                    p={3}
                    color="white"
                    mb={4}
                    bg="black"
                >
                    {description}
                </Box>
            </Collapse>
        </>
    );
};
